import { useState, useEffect } from 'react';
import { X, BookOpen, Clock, Award, Calendar, CheckCircle, FileText, Target, Users } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface LearningOutcome {
  text: string;
  esco_skill_uri?: string;
  esco_skill_label?: string;
}

interface LearningOutcomes {
  knowledge?: LearningOutcome[];
  skills?: LearningOutcome[];
  competence?: LearningOutcome[];
}

interface ContentModule {
  title: string;
  description?: string;
  hours?: number;
  learning_outcomes?: string[];
}

interface StudyPlanVersion {
  id: string;
  study_plan_id: string;
  version_number: number;
  title: string;
  description?: string;
  ects_credits: number;
  estimated_hours?: number;
  learning_outcomes?: LearningOutcomes;
  content_modules?: ContentModule[];
  assessment_criteria?: string;
  target_group?: string;
  admission_requirements?: string;
  metadata?: {
    eqf_level?: number;
    language?: string;
    country?: string;
  };
  approved_at?: string;
  approved_by_name?: string;
}

interface StudyPlanVersionPreviewProps {
  studyPlanId: string;
  versionId?: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function StudyPlanVersionPreview({
  studyPlanId,
  versionId,
  isOpen,
  onClose
}: StudyPlanVersionPreviewProps) {
  const [version, setVersion] = useState<StudyPlanVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && studyPlanId) {
      fetchVersion();
    }
  }, [isOpen, studyPlanId, versionId]);

  const fetchVersion = async () => {
    try {
      setLoading(true);
      setError(null);

      // If we have a specific version ID, fetch that version
      // Otherwise, fetch the latest approved version
      let url = `/api/study-plans/${studyPlanId}/versions`;

      const response = await authenticatedFetch(url);
      const data = await response.json();

      if (data.success && data.versions && data.versions.length > 0) {
        // If versionId specified, find that version; otherwise use latest
        if (versionId) {
          const specificVersion = data.versions.find((v: StudyPlanVersion) => v.id === versionId);
          setVersion(specificVersion || data.versions[0]);
        } else {
          // Get the latest approved version (first in array, sorted by version_number DESC)
          setVersion(data.versions[0]);
        }
      } else {
        setError('Ingen godkjent versjon funnet');
      }
    } catch (err) {
      console.error('Error fetching version:', err);
      setError('Kunne ikke hente studieplanen');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const renderLearningOutcomes = (outcomes: LearningOutcomes) => {
    const categories = [
      { key: 'knowledge', label: 'Kunnskaper', icon: BookOpen },
      { key: 'skills', label: 'Ferdigheter', icon: Target },
      { key: 'competence', label: 'Generell kompetanse', icon: Users }
    ] as const;

    return (
      <div className="space-y-4">
        {categories.map(({ key, label, icon: Icon }) => {
          const items = outcomes[key];
          if (!items || items.length === 0) return null;

          return (
            <div key={key}>
              <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-1 mb-2">
                <Icon size={14} />
                <span>{label}</span>
              </h4>
              <ul className="space-y-1">
                {items.map((item, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start">
                    <CheckCircle size={12} className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                    <span>{typeof item === 'string' ? item : item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-4 md:inset-10 lg:inset-20 bg-white rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-lg">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Forhåndsvisning av studieplan
            </h2>
            {version && (
              <p className="text-sm text-gray-500">
                Versjon {version.version_number}
                {version.approved_at && (
                  <span> - Godkjent {new Date(version.approved_at).toLocaleDateString('nb-NO')}</span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kf-primary"></div>
              <span className="ml-3 text-gray-600">Laster studieplan...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchVersion}
                className="mt-4 text-kf-primary hover:underline"
              >
                Prøv igjen
              </button>
            </div>
          ) : version ? (
            <div className="space-y-6">
              {/* Title and Description */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{version.title}</h3>
                {version.description && (
                  <p className="text-gray-600">{version.description}</p>
                )}
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 text-blue-700">
                    <Award size={18} />
                    <span className="text-sm font-medium">ECTS</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {version.ects_credits}
                  </p>
                </div>

                {version.estimated_hours && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-green-700">
                      <Clock size={18} />
                      <span className="text-sm font-medium">Timer</span>
                    </div>
                    <p className="text-2xl font-bold text-green-900 mt-1">
                      {version.estimated_hours}
                    </p>
                  </div>
                )}

                {version.metadata?.eqf_level && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-purple-700">
                      <FileText size={18} />
                      <span className="text-sm font-medium">EQF/NKR-nivå</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-900 mt-1">
                      {version.metadata.eqf_level}
                    </p>
                  </div>
                )}

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 text-orange-700">
                    <Calendar size={18} />
                    <span className="text-sm font-medium">Versjon</span>
                  </div>
                  <p className="text-2xl font-bold text-orange-900 mt-1">
                    v{version.version_number}
                  </p>
                </div>
              </div>

              {/* Target Group */}
              {version.target_group && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                    <Users size={16} />
                    <span>Målgruppe</span>
                  </h4>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                    {version.target_group}
                  </p>
                </div>
              )}

              {/* Admission Requirements */}
              {version.admission_requirements && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Opptakskrav</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                    {version.admission_requirements}
                  </p>
                </div>
              )}

              {/* Learning Outcomes */}
              {version.learning_outcomes && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <Target size={16} />
                    <span>Læringsutbytte</span>
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {renderLearningOutcomes(version.learning_outcomes)}
                  </div>
                </div>
              )}

              {/* Content Modules */}
              {version.content_modules && version.content_modules.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                    <BookOpen size={16} />
                    <span>Innholdsmoduler ({version.content_modules.length})</span>
                  </h4>
                  <div className="space-y-2">
                    {version.content_modules.map((module, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-gray-900">{module.title}</h5>
                          {module.hours && (
                            <span className="text-sm text-gray-500">{module.hours} timer</span>
                          )}
                        </div>
                        {module.description && (
                          <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assessment Criteria */}
              {version.assessment_criteria && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Vurderingskriterier</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded p-3 whitespace-pre-wrap">
                    {version.assessment_criteria}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Ingen data tilgjengelig
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
}
